class ButtonAnimation {
    constructor(element) {
        // don't initialize if user has set prefers-reduced-motion or if its a mobile device
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const isMobile = window.matchMedia('(max-width: 768px)').matches;

        if (reducedMotion || isMobile) {
            return;
        }

        this.element = element;

        // Default add has-motion class to remove with the prefers-reduced-motion media query
        this.element.classList.add('has-motion');
        this.element.classList.add('button-animation');
    }

    get width() {
        return this.element.offsetWidth;
    }

    createElement(tag, classNames, styles = {}) {
        const el = document.createElement(tag);

        el.setAttribute('aria-hidden', 'true');

        if (classNames) {
            if (Array.isArray(classNames)) {
                el.classList.add(...classNames);
            } else {
                el.classList.add(classNames);
            }
        }

        Object.assign(el.style, styles);

        return el;
    }
}

class DissolveButtonAnimation extends ButtonAnimation {
    constructor(element, options = {}) {
        super(element);

        this.name = 'dissolve';
        this._defaults = {
            baseDelay: 1,
            blockWidth: 17,
            blockHeight: 17,
            blocksPerColumn: 3,
            randomDelayMax: 200,
            staggerRowDelay: 20,
            staggerColumnDelay: 10,
        };
        this.options = { ...this._defaults, ...options };

        this.init();
        this.render();
    }

    init() {
        this.element.classList.add(`button-animation--${this.name}`);
    }

    render() {
        // create a 3 block high grid of blocks in the button
        const gridContainer = this.createElement('span', 'animation-grid');

        // Adjust these values to change block size and layout
        const blockAttributes = {
            'height': `${this.options.blockHeight}px`,
            'width': `${this.options.blockWidth}px`,
        }

        const rowsNeeded = Math.ceil(this.element.offsetHeight / this.options.blockHeight);
        const totalRows = Math.max(rowsNeeded, this.options.blocksPerColumn);
        gridContainer.style.gridTemplateRows = `repeat(${totalRows}, ${this.options.blockHeight}px)`;
        gridContainer.style.gridAutoFlow = 'column';
        
        // Calculate number of columns and rows based on button size
        const blockSize = this.options.blockWidth;
        const columns = Math.ceil(this.width / blockSize);
        const rows = Math.max(Math.ceil(this.element.offsetHeight / blockSize), this.options.blocksPerColumn);

        gridContainer.style.display = 'grid';
        gridContainer.style.gridTemplateColumns = `repeat(${columns}, ${blockSize}px)`;
        gridContainer.style.gridTemplateRows = `repeat(${rows}, ${blockSize}px)`;
        gridContainer.style.gridAutoFlow = 'row';

        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < columns; col++) {
                const block = this.createElement('span', 'animation-block');
                
                for (const [key, value] of Object.entries(blockAttributes)) {
                    block.style[key] = value;
                }
                // Make transitionDelay depend on row and column for more interesting stagger
                const randomDelay = Math.floor(Math.random() * this.options.randomDelayMax);
                const stagger = (row * this.options.staggerRowDelay) + (col * this.options.staggerColumnDelay);
                block.style.transitionDelay = this.options.baseDelay + randomDelay + stagger + 'ms';
                gridContainer.appendChild(block);
            }
        }

        this.element.appendChild(gridContainer);
    }
}

class RollupTextButtonAnimation extends ButtonAnimation {
    constructor(element, options = {}) {
        super(element);

        this.name = 'rollup-text';
        this._defaults = {
            textSelector: null,
        };
        this.options = { ...this._defaults, ...options };

        if (!this.element) return;

        this.init();
        this.render();
    }

    init() {
        this.element.classList.add(`button-animation--${this.name}`);
    }

    render() {
        let text = '';
        let icon = null;

        const animationContainer = this.createElement('span', 'button-animation-container');
        const textLayer = this.createElement('span', 'layer__text');

        text = this.element.textContent.trim();
        icon = this.element.querySelector('.icon');

        for (let i = text.length - 1; i >= 0; i--) {
            const char = text[i];
            const charSpan = this.createElement('span', 'animation-char');
            const staggerDelay = i * 10;

            // If the character is a space, add a special class and preserve spacing
            if (char === ' ') {
                charSpan.classList.add('animation-char--space');
                charSpan.innerHTML = '&nbsp;';
                charSpan.style.transitionDelay = `${staggerDelay}ms`;
                textLayer.prepend(charSpan);
                continue;
            }

            const staticCharSpan = this.createElement('span', ['char', 'char--static']);
            staticCharSpan.textContent = char;
            staticCharSpan.style.transitionDelay = `${staggerDelay}ms`;
            charSpan.appendChild(staticCharSpan);

            const cloneCharSpan = this.createElement('span', ['char', 'char--clone']);
            cloneCharSpan.textContent = char;
            cloneCharSpan.style.transitionDelay = `${staggerDelay}ms`;
            charSpan.appendChild(cloneCharSpan);

            textLayer.prepend(charSpan);
        }

        animationContainer.appendChild(textLayer);

        if (icon) {
            const iconClone = icon.cloneNode(true);
            iconClone.classList.add('icon--clone');
            animationContainer.appendChild(iconClone);
        }

        this.element.appendChild(animationContainer);
    }
}

export { RollupTextButtonAnimation, DissolveButtonAnimation };